import { z } from 'zod';
import { PlaceCategoryDto, PlaceCategoryGroupEnum } from './place-category';

/** Slugs «bookable»-категорий мест. Зарезервировано под будущую реальную бронь (резервация). */
export const BOOKABLE_PLACE_SLUGS = new Set([
  'restaurant',
  'bar',
  'coffee',
  'fastfood',
  'sweets',
  'tennis',
  'pingpong',
  'volleyball',
  'fitness',
  'golf',
  'water',
]);

// Telegram/IG username normalizer — strips leading '@', trims, lowercases.
const usernameNorm = z
  .preprocess(
    (v) => {
      if (typeof v !== 'string') return v;
      const trimmed = v.trim();
      const noAt = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
      return noAt.toLowerCase();
    },
    z
      .string()
      .min(1)
      .max(32)
      .regex(/^[a-z0-9_.]+$/, 'invalid username'),
  )
  .nullable()
  .optional();

// Base shape without defaults — used to derive the partial update.
const placeCreateBase = z.object({
  cityId: z.uuid(),
  name: z.string().min(1).max(120).trim(),
  slug: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9-]*$/, 'lowercase, digits, dashes')
    .optional(),
  description: z.string().max(2000).nullable().optional(),

  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),

  addressText: z.string().min(1).max(300).trim(),

  websiteUrl: z.url().max(2000).nullable().optional(),
  instagramUsername: usernameNorm,
  managerTelegramUsername: usernameNorm,
  openingHoursText: z.string().max(200).nullable().optional(),

  coverImageUrl: z.url().max(2000).nullable().optional(),
  coverStorageKey: z.string().max(500).nullable().optional(),

  googlePlaceId: z.string().max(200).nullable().optional(),
  lastSyncedAt: z.iso.datetime().nullable().optional(),

  categoryIds: z.array(z.uuid()).max(10),
});

export const PlaceCreateInput = placeCreateBase.extend({
  categoryIds: z.array(z.uuid()).max(10).default([]),
});
export type PlaceCreateInput = z.infer<typeof PlaceCreateInput>;

export const PlaceUpdateInput = placeCreateBase.partial().extend({
  isActive: z.boolean().optional(),
});
export type PlaceUpdateInput = z.infer<typeof PlaceUpdateInput>;

export const PlaceListQuery = z.object({
  cityId: z.uuid(),
  categoryId: z.uuid().optional(),
  group: PlaceCategoryGroupEnum.optional(),
  search: z.string().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type PlaceListQuery = z.infer<typeof PlaceListQuery>;

export const PlaceMapPreviewQuery = z.object({
  cityId: z.uuid(),
  q: z.string().trim().min(1).max(200),
});
export type PlaceMapPreviewQuery = z.infer<typeof PlaceMapPreviewQuery>;

export const PlaceMapPreviewItemDto = z.object({
  /** Опаковый id пина: Place.id для локальных совпадений, googlePlaceId для остальных. */
  id: z.string().min(1),
  /** Задан только для мест, уже существующих в локальной базе — фронт линкует их как обычный placeId. */
  placeId: z.uuid().optional(),
  name: z.string().min(1),
  address: z.string(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type PlaceMapPreviewItemDto = z.infer<typeof PlaceMapPreviewItemDto>;

export const PlaceMapPreviewResponse = z.object({
  center: z.object({ lat: z.number(), lng: z.number() }).nullable(),
  items: z.array(PlaceMapPreviewItemDto),
});
export type PlaceMapPreviewResponse = z.infer<typeof PlaceMapPreviewResponse>;

export const AdminPlaceListQuery = PlaceListQuery.extend({
  cityId: z.uuid().optional(),
  isActive: z.coerce.boolean().optional(),
});
export type AdminPlaceListQuery = z.infer<typeof AdminPlaceListQuery>;

export const PlaceDto = z.object({
  id: z.uuid(),
  cityId: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  addressText: z.string(),
  websiteUrl: z.string().nullable(),
  instagramUsername: z.string().nullable(),
  managerTelegramUsername: z.string().nullable(),
  openingHoursText: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  googlePlaceId: z.string().nullable().optional(),
  lastSyncedAt: z.iso.datetime().nullable().optional(),
  categories: z.array(PlaceCategoryDto),
  likeCount: z.number().int(),
  isLikedByMe: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PlaceDto = z.infer<typeof PlaceDto>;

export const PlaceListItemDto = PlaceDto.omit({ description: true });
export type PlaceListItemDto = z.infer<typeof PlaceListItemDto>;

export const PlaceListResponse = z.object({
  items: z.array(PlaceListItemDto),
  nextCursor: z.string().nullable(),
});
export type PlaceListResponse = z.infer<typeof PlaceListResponse>;

export const AdminPlaceDto = PlaceDto.extend({
  isActive: z.boolean(),
  createdById: z.uuid().nullable(),
});
export type AdminPlaceDto = z.infer<typeof AdminPlaceDto>;

// Thin-ref на Place для использования в EventDto.place.
export const PlaceThinRefDto = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  coverImageUrl: z.string().nullable(),
  // Координаты места — для кнопок такси (Grab/Maxim/маршрут) в карточке события.
  lat: z.number(),
  lng: z.number(),
});
export type PlaceThinRefDto = z.infer<typeof PlaceThinRefDto>;

export const PlaceMatchCandidateDto = z.object({
  id: z.uuid(),
  name: z.string(),
  addressText: z.string(),
  similarity: z.number().min(0).max(1),
  matchedField: z.enum(['name', 'addressText']),
});
export type PlaceMatchCandidateDto = z.infer<typeof PlaceMatchCandidateDto>;

export const PlaceMatchResponse = z.object({
  candidates: z.array(PlaceMatchCandidateDto),
  autoLinkPlaceId: z.uuid().nullable(),
});
export type PlaceMatchResponse = z.infer<typeof PlaceMatchResponse>;

export const PlaceMatchQuery = z.object({
  cityId: z.uuid(),
  q: z.string().min(1).max(200),
});
export type PlaceMatchQuery = z.infer<typeof PlaceMatchQuery>;
